import {
  add,
  distance,
  distancePointToSegment,
  insetPolygon,
  interiorPoint,
  midpoint,
  normalize,
  pointInPolygon,
  polygonArea,
  polygonCentroid,
  polygonEdges,
  raySegmentHit,
  regionArea,
  scale,
  segmentFaces,
  sub,
  subtractPolygons,
  worldToLocal,
  type Segment,
  type Vec2,
} from '@indusketch/geometry';
import { buildingCorners } from '../building.js';
import type { Building, ProjectDocument, Zone } from '../types.js';
import { fillText, modelTexts } from '../texts.js';

/** Largest gap between a partition wall and the next wall that still counts as closed. */
export const SECTION_MAX_GAP = 1;
const TOUCH = 0.01;
const MIN_AREA = 0.25;

export interface HallSection {
  readonly key: string;
  readonly name: string;
  readonly outer: readonly Vec2[];
  /** Offices, rooms or enclosed sections inside this one. */
  readonly holes: readonly (readonly Vec2[])[];
  /** Measured up to the wall centerlines. */
  readonly area: number;
  readonly label: Vec2;
}

export interface HallSections {
  readonly sections: readonly HallSection[];
  /** Gaps closed virtually between the end of a partition wall and the next wall. */
  readonly links: readonly (readonly [Vec2, Vec2])[];
}

const NONE: HallSections = { sections: [], links: [] };

export const hallInterior = (h: Building): Vec2[] =>
  h.footprint
    ? insetPolygon(h.footprint, h.wallThickness)
    : buildingCorners({
        center: h.center,
        rotation: h.rotation,
        width: h.width - 2 * h.wallThickness,
        depth: h.depth - 2 * h.wallThickness,
      });

/** A zone covering the whole section (e.g. created with "Als Zone übernehmen"). */
export function sectionZone(
  doc: Pick<ProjectDocument, 'zones'>,
  hallId: string,
  s: HallSection,
): Zone | undefined {
  return Object.values(doc.zones).find(
    (z) =>
      z.buildingId === hallId &&
      z.level === 0 &&
      Math.abs(polygonArea(z.polygon) - s.area) <= 0.01 * s.area &&
      pointInPolygon(polygonCentroid(z.polygon), s.outer),
  );
}

/** A, B, …, Z, AA, AB, … */
function letters(i: number): string {
  const s = String.fromCharCode(65 + (i % 26));
  return i < 26 ? s : letters(Math.floor(i / 26) - 1) + s;
}

/**
 * Parts of a hall's ground floor separated by freely drawn partition walls. Built-in offices
 * and rooms are cut out. Empty unless the walls divide the hall into at least two parts.
 */
export function hallSections(
  doc: Pick<ProjectDocument, 'buildings' | 'walls' | 'rooms'>,
  hallId: string,
): HallSections {
  const hall = doc.buildings[hallId];
  if (
    hall?.kind !== 'hall' ||
    hall.width <= 2 * hall.wallThickness ||
    hall.depth <= 2 * hall.wallThickness
  )
    return NONE;
  // Sections are derived from a rectangular interior; drawn outlines do not have them yet.
  if (hall.footprint) return NONE;
  const interior = hallInterior(hall);
  const walls: Segment[] = Object.values(doc.walls)
    .filter(
      (w) =>
        w.ownerId === null &&
        w.level === 0 &&
        distance(w.start, w.end) > TOUCH &&
        pointInPolygon(midpoint(w.start, w.end), interior),
    )
    .map((w) => ({ a: w.start, b: w.end }));
  if (walls.length === 0) return NONE;
  const obstacles = [
    ...Object.values(doc.buildings)
      .filter((b) => b.office?.placement === 'indoor' && pointInPolygon(b.center, interior))
      .map((b) => buildingCorners(b)),
    ...Object.values(doc.rooms)
      .filter((r) => r.buildingId === hall.id && r.level === 0)
      .map((r) => r.polygon),
  ];
  const fixed = [...polygonEdges(interior), ...obstacles.flatMap(polygonEdges)];

  const links: [Vec2, Vec2][] = [];
  const dividers = walls.map((s, i) => {
    const others = [...fixed, ...walls.filter((_, j) => j !== i)];
    const reach = (p: Vec2, dir: Vec2): Vec2 => {
      if (!pointInPolygon(p, interior) || others.some((o) => distancePointToSegment(p, o) <= TOUCH))
        return p;
      let best = Infinity;
      for (const o of others) {
        const t = raySegmentHit(p, dir, o);
        if (t !== null && t < best) best = t;
      }
      if (best > SECTION_MAX_GAP) return p;
      const q = add(p, scale(dir, best));
      links.push([p, q]);
      return q;
    };
    const d = normalize(sub(s.b, s.a));
    return { a: reach(s.a, scale(d, -1)), b: reach(s.b, d) };
  });

  const inside = (p: Vec2) =>
    pointInPolygon(p, interior) && obstacles.every((o) => !pointInPolygon(p, o));
  const enclosed = (outer: readonly Vec2[]) =>
    obstacles.filter((o) => o.every((p) => pointInPolygon(p, outer)));
  const faces = segmentFaces([...fixed, ...dividers], TOUCH)
    .map((outer) => ({ outer, probe: interiorPoint(outer, enclosed(outer)) }))
    .filter((f) => inside(f.probe));
  const parts = faces
    .map((f) => {
      const nested = faces
        .filter((o) => o !== f && pointInPolygon(o.probe, f.outer))
        .map((o) => o.outer);
      const regions = subtractPolygons(f.outer, [...obstacles, ...nested]);
      const holes = regions.flatMap((r) => r.holes);
      return {
        outer: f.outer,
        holes,
        area: regions.reduce((s, r) => s + regionArea(r), 0),
        label: interiorPoint(f.outer, holes),
      };
    })
    .filter((p) => p.area >= MIN_AREA);
  if (parts.length < 2) return NONE;

  // Reading order in the hall's own frame: north to south, then west to east.
  const frame = { position: hall.center, rotation: hall.rotation };
  const local = new Map(parts.map((p) => [p, worldToLocal(polygonCentroid(p.outer), frame)]));
  parts.sort((p, q) => {
    const a = local.get(p) ?? p.label;
    const b = local.get(q) ?? q.label;
    return Math.abs(a.y - b.y) > 1 ? b.y - a.y : a.x - b.x;
  });
  return {
    sections: parts.map((p, i) => ({
      key: `${hall.id}:${letters(i)}`,
      name: fillText(modelTexts().sectionName, { letter: letters(i) }),
      ...p,
    })),
    links,
  };
}
